/**
 * 20261022 [IOS-RELAY] iOS 앱 relay - 로컬 미리보기 연결(웹 = answer 측)
 * - Android web-relay(src/lib/relay/*)와 분리된 iOS 전용 모듈. 같은 메시지 이름/순서를 사용한다.
 *   start -> (앱) offer 이벤트 -> answer -> candidate 교환 -> state 이벤트
 * - 1단계: 연결만 만든다. 스크린 모드는 기존 '앱 연결' 이미지 유지(영상 표시 없음).
 *   역할별 수신 스트림은 2단계(카메라)·3단계(마이캠)에서 화면에 붙인다.
 * - 앱이 relay 를 지원하지 않거나(구버전 앱) 개발 버전이 아니면 아무 동작도 하지 않는다.
 * - 20261023 2단계: 화면 바꿈 버튼 = 스크린 > 전면 > 후면 > 스크린 (switchVideo -> liveAck).
 *   카메라 영상은 앱이 교사에게 송출하고, 웹은 미리보기(mainVideo)만 표시한다. 웹 카메라/마이크/Producer 없음.
 */
import { ensureIOSRelayReady, isIOSRelayFeature, requestIOSRelay, onIOSRelayEvent } from "./IOSRelayAdapter";

const LOG = "[IOS-RELAY]";
const TRACK_WAIT_MS = 12000;
const LIVE_ACK_TIMEOUT_MS = 10000; // 20261023 2단계

const state = {
    sessionId: null,
    connectionGeneration: 0,
    pc: null,
    roleByMid: {},
    streams: {},            // role -> MediaStream (mainVideo, myCam)
    pendingCandidates: [],
    unsubscribe: null,
    starting: null,
    t0: 0,
    ice: "none",
};

// 20261023 [IOS-RELAY] 2단계 - 카메라 전환 상태
const cam = {
    mode: "SCREEN",     // SCREEN / CAMERA
    facing: null,       // front / back
    opGen: 0,
    busy: false,
    waiters: {},        // operationGeneration -> resolve
    myCamOn: false,     // 20261026 3단계 - 앱 카메라 MyCam 표시 중
    myCamBusy: false,
};

const resetCam = () => {
    Object.keys(cam.waiters).forEach((k) => { try { cam.waiters[k]({ result: "session-closed" }); } catch (e) { } });
    cam.mode = "SCREEN";
    cam.facing = null;
    cam.opGen = 0;
    cam.busy = false;
    cam.waiters = {};
    cam.myCamOn = false;
    cam.myCamBusy = false;
};

const elapsed = () => `+${Date.now() - state.t0}ms`;

const newId = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const closePc = () => {
    if (state.pc) {
        try { state.pc.close(); } catch (e) { }
    }
    state.pc = null;
    state.roleByMid = {};
    state.streams = {};
    state.pendingCandidates = [];
    state.ice = "none";
};

const waitGathering = (pc, ms) => new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") { resolve(); return; }
    const to = setTimeout(resolve, ms);
    pc.addEventListener("icegatheringstatechange", () => {
        if (pc.iceGatheringState === "complete") { clearTimeout(to); resolve(); }
    });
});

const handleOffer = async (ev) => {
    if (ev.sessionId !== state.sessionId) return;
    if (ev.connectionGeneration <= state.connectionGeneration && state.pc) {
        console.log(`${LOG} 이전 세대 offer 무시 gen[${ev.connectionGeneration}]`);
        return;
    }
    closePc();
    state.connectionGeneration = ev.connectionGeneration;
    (ev.roles || []).forEach((r) => { if (r.mid !== "") state.roleByMid[r.mid] = r.role; });

    const pc = new RTCPeerConnection({ iceServers: [], bundlePolicy: "max-bundle" });
    state.pc = pc;
    const gen = ev.connectionGeneration;
    const sid = ev.sessionId;

    pc.ontrack = (e) => {
        const mid = e.transceiver ? e.transceiver.mid : null;
        const role = state.roleByMid[mid] || `mid:${mid}`;
        if (e.track.kind === "video") {
            state.streams[role] = (e.streams && e.streams[0]) || new MediaStream([e.track]);
        }
        console.log(`${LOG} ontrack role[${role}] kind[${e.track.kind}] ${elapsed()}`);
    };
    pc.onicecandidate = (e) => {
        if (!e.candidate || state.pc !== pc) return;
        requestIOSRelay("candidate", {
            sessionId: sid, connectionGeneration: gen,
            candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex,
        });
    };
    pc.oniceconnectionstatechange = () => {
        if (state.pc !== pc) return;
        state.ice = pc.iceConnectionState;
        console.log(`${LOG} 웹 ICE ${pc.iceConnectionState} ${elapsed()}`);
    };

    try {
        await pc.setRemoteDescription({ type: "offer", sdp: ev.sdp });
        const pend = state.pendingCandidates.splice(0);
        pend.forEach((c) => pc.addIceCandidate(c).catch(() => { }));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await waitGathering(pc, 1000);
        if (state.pc !== pc) return;
        const res = await requestIOSRelay("answer", { sessionId: sid, connectionGeneration: gen, sdp: pc.localDescription.sdp });
        console.log(`${LOG} answer 전송 ${elapsed()} ->`, res && res.result);
        setTimeout(() => {
            if (state.pc === pc && Object.keys(state.streams).length < 2) {
                console.log(`${LOG} ${TRACK_WAIT_MS}ms 내 영상 트랙 미수신 - roles`, Object.keys(state.streams));
            }
        }, TRACK_WAIT_MS);
    } catch (e) {
        console.log(`${LOG} offer 처리 오류`, e);
    }
};

const handleEvent = (ev) => {
    if (!ev || !ev.type) return;
    switch (ev.type) {
        case "offer":
            handleOffer(ev);
            break;
        case "candidate": {
            if (ev.sessionId !== state.sessionId || ev.connectionGeneration !== state.connectionGeneration) return;
            const c = { candidate: ev.candidate, sdpMid: ev.sdpMid, sdpMLineIndex: ev.sdpMLineIndex };
            if (state.pc && state.pc.remoteDescription) state.pc.addIceCandidate(c).catch(() => { });
            else state.pendingCandidates.push(c);
            break;
        }
        case "state":
            console.log(`${LOG} 앱 ${ev.kind} ${ev.state} gen[${ev.connectionGeneration}]`);
            break;
        case "liveAck": { // 20261023 2단계
            if (ev.sessionId !== state.sessionId) return;
            console.log(`${LOG} liveAck gen[${ev.operationGeneration}] result[${ev.result}] mode[${ev.mode}] facing[${ev.cameraFacing}] ${ev.inputWidth}x${ev.inputHeight} +${ev.elapsedMs}ms`);
            const w = cam.waiters[ev.operationGeneration];
            if (w) { delete cam.waiters[ev.operationGeneration]; w(ev); }
            break;
        }
        case "liveCapture": // 20261023 2단계 - 카메라 인터럽트(백그라운드 등)는 로그만
            console.log(`${LOG} 앱 카메라 ${ev.kind} ${ev.message || ""}`);
            break;
        case "error":
        case "stopped":
            console.log(`${LOG} 앱 ${ev.type}`, ev);
            if (ev.sessionId === state.sessionId) {
                closePc();
                resetCam();
                state.sessionId = null;
            }
            break;
        default:
            console.log(`${LOG} 이벤트`, ev);
    }
};

/**
 * 로컬 연결 시작 (이미 시작/진행 중이면 그대로 둔다).
 * @param {String} reason - 호출 사유(로그용)
 * @returns {Promise<Boolean>} relay 연결을 시작(또는 이미 사용 중)했으면 true
 */
export const startIOSRelaySession = async (reason) => {
    if (state.sessionId) return true;
    if (state.starting) return state.starting;

    state.starting = (async () => {
        const doc = await ensureIOSRelayReady();
        if (!doc || !isIOSRelayFeature("localPeer")) return false;

        if (!state.unsubscribe) state.unsubscribe = onIOSRelayEvent(handleEvent);
        state.t0 = Date.now();
        state.sessionId = newId("ios-relay");
        state.connectionGeneration = 0;
        console.log(`${LOG} start(${reason}) sid[${state.sessionId}]`);
        const res = await requestIOSRelay("start", { sessionId: state.sessionId, requestId: newId("req"), roles: ["mainVideo", "myCam"], live: true });
        const ok = res && (res.result === "accepted" || res.result === "idempotent");
        if (!ok) {
            console.log(`${LOG} start 실패`, res);
            state.sessionId = null;
        }
        return ok;
    })();

    try {
        return await state.starting;
    } finally {
        state.starting = null;
    }
};

/**
 * 로컬 연결 종료 (수업 종료/핸즈업 종료).
 * @param {String} reason - 호출 사유(로그용)
 */
export const stopIOSRelaySession = async (reason) => {
    const sid = state.sessionId;
    if (!sid) return;
    console.log(`${LOG} stop(${reason}) sid[${sid}]`);
    state.sessionId = null;
    closePc();
    resetCam(); // 20261023 2단계
    await requestIOSRelay("stop", { sessionId: sid, requestId: newId("req") });
};

// ---------------------------------------------------------------------------
// 20261023 [IOS-RELAY] 2단계 - 스크린 <-> 앱 카메라 전환
// ---------------------------------------------------------------------------

/** 앱 카메라 전환을 쓸 수 있는 상태인지 (세션 연결 + 앱이 camera 지원) */
export const isIOSRelayCameraReady = () =>
    !!state.sessionId && !!state.pc && (state.ice === "connected" || state.ice === "completed") && isIOSRelayFeature("camera");

/**
 * 20261023 [IOS-RELAY] 앱이 마이크/영상을 송출하는 relay 사용 중인지 (true 면 웹은 마이크를 열지 않는다)
 * - WKWebView 마이크 캡처가 ReplayKit 시스템 소리를 망가뜨리므로 relay 중에는 항상 앱 마이크만 사용
 */
export const isIOSRelayNativeMic = () => !!state.sessionId && isIOSRelayFeature("nativePublish");

/** 현재 앱 카메라 모드 (SCREEN / CAMERA) */
export const getIOSRelayCameraMode = () => ({ mode: cam.mode, facing: cam.facing });

const sendSwitch = (mode, facing, extra = {}) => new Promise((resolve) => {
    const gen = ++cam.opGen;
    const to = setTimeout(() => {
        if (cam.waiters[gen]) { delete cam.waiters[gen]; resolve({ result: "timeout" }); }
    }, LIVE_ACK_TIMEOUT_MS);
    cam.waiters[gen] = (ack) => { clearTimeout(to); resolve(ack); };
    requestIOSRelay("switchVideo", {
        sessionId: state.sessionId, role: "video", mode, facing,
        operationGeneration: gen, operationId: newId("op"),
        ...extra, // 20261026 3단계 - { role: "myCam", detach }
    }).then((res) => {
        const r = res && res.result;
        if (r !== "accepted" && r !== "idempotent" && cam.waiters[gen]) {
            delete cam.waiters[gen];
            clearTimeout(to);
            resolve(res || { result: "error" });
        }
    });
});

const applyMode = (ctx, mode, facing) => {
    const info = ctx.info;
    cam.mode = mode;
    cam.facing = mode === "CAMERA" ? facing : null;
    info.viewKind = mode;
    info.beforeViewKind = null;
    if (info.videoInputInfo) info.videoInputInfo.selectedIndex = mode === "CAMERA" ? (facing === "back" ? 1 : 0) : -1;

    const stream = getIOSRelayStream("mainVideo");
    if (mode === "CAMERA") {
        if (info.videoEl && stream) {
            if (info.videoEl.srcObject !== stream) info.videoEl.srcObject = stream;
            info.videoEl.muted = true;
            const p = info.videoEl.play();
            if (p && p.catch) p.catch((e) => console.log(`${LOG} 미리보기 play 오류`, e));
        } else {
            console.log(`${LOG} 미리보기 연결 불가 - videoEl[${!!info.videoEl}] stream[${!!stream}]`);
        }
        if (info.handsUpFunc) info.handsUpFunc("viewKind_camera");
    } else {
        if (info.videoEl && stream && info.videoEl.srcObject === stream) info.videoEl.srcObject = null;
        if (ctx.setScreenImage) ctx.setScreenImage("connect");
    }
    // 기존 iOS "카메라 중단/재개(다른 앱 사용 중)" 패킷 등록/해제 재사용
    if (ctx.syncCameraPackets) ctx.syncCameraPackets(info.liveSeq, info.userSeq, mode === "CAMERA");
};

/**
 * 화면 바꿈 버튼 1회 처리 (스크린 > 전면 > 후면 > 스크린).
 * @param {Object} ctx - { info: handsUpClientInfo, setBtnDisabled, clearMyCam, setScreenImage, syncCameraPackets }
 * @returns {Promise<Boolean>} 처리했으면 true (false 면 기존 경로 진행)
 */
export const switchIOSRelayVideo = async (ctx) => {
    if (!isIOSRelayCameraReady()) return false;
    if (cam.busy) { console.log(`${LOG} 전환 진행 중 - 무시`); return true; }
    cam.busy = true;
    if (ctx.setBtnDisabled) ctx.setBtnDisabled(true);
    try {
        if (cam.myCamOn) await myCamOff(ctx, "video-switch"); // 20261026 3단계 - MyCam 먼저 정리(카메라 반환 후 전환)
        if (ctx.clearMyCam) ctx.clearMyCam();
        let next;
        if (cam.mode !== "CAMERA") next = { mode: "CAMERA", facing: "front" };
        else if (cam.facing === "front") next = { mode: "CAMERA", facing: "back" };
        else next = { mode: "SCREEN", facing: "front" };

        const t0 = Date.now();
        console.log(`${LOG} 전환 요청 ${cam.mode}/${cam.facing} -> ${next.mode}/${next.facing}`);
        const ack = await sendSwitch(next.mode, next.facing);
        if (ack && ack.result === "done") {
            applyMode(ctx, ack.mode || next.mode, ack.cameraFacing || next.facing);
            console.log(`${LOG} 전환 완료 ${cam.mode}/${cam.facing} (${Date.now() - t0}ms)`);
        } else {
            console.log(`${LOG} 전환 실패`, ack);
            // 카메라 실패/시간초과 → 스크린으로 정리
            if (next.mode === "CAMERA") {
                const back = await sendSwitch("SCREEN", "front");
                console.log(`${LOG} 스크린 복귀`, back && back.result);
            }
            applyMode(ctx, "SCREEN", null);
        }
    } catch (e) {
        console.log(`${LOG} 전환 오류`, e);
    } finally {
        cam.busy = false;
        if (ctx.setBtnDisabled) ctx.setBtnDisabled(false);
    }
    return true;
};

/** 역할별 수신 스트림 (mainVideo / myCam) */
export const getIOSRelayStream = (role) => state.streams[role] || null;

/** iOS relay 세션 사용 중 여부 */
export const isIOSRelayActive = () => !!state.sessionId;

// ---------------------------------------------------------------------------
// 20261026 [IOS-RELAY] 3단계 - MyCam (SCREEN 에서만, 앱 카메라 미리보기 전용 / 교사 송출 없음)
// - 수신 트랙은 로컬 연결의 MyCam 자리이므로 stop() 하지 않고 화면에서만 뗀다.
// ---------------------------------------------------------------------------

const detachMyCamEl = (info) => {
    const stream = getIOSRelayStream("myCam");
    if (info.myCamEl && stream && info.myCamEl.srcObject === stream) info.myCamEl.srcObject = null;
};

const myCamOff = async (ctx, reason) => {
    const info = ctx.info;
    const wasOn = cam.myCamOn;
    cam.myCamOn = false;
    detachMyCamEl(info);
    if (info.videoInputInfo) {
        info.videoInputInfo.isMyCamRunning = false;
        info.videoInputInfo.myCamIndex = -1;
        info.videoInputInfo.myCamMediaInfo = { deviceId: null, groupId: null, label: null };
    }
    if (!wasOn || !state.sessionId) return;
    const t0 = Date.now();
    const ack = await sendSwitch("SCREEN", "front", { role: "myCam", detach: true });
    console.log(`${LOG} MyCam 끄기(${reason}) result[${ack && ack.result}] deviceReturned[${ack && ack.deviceReturned}] (${Date.now() - t0}ms)`);
};

const myCamOn = async (ctx) => {
    const info = ctx.info;
    if (cam.mode !== "SCREEN") {
        console.log(`${LOG} MyCam 은 스크린 모드에서만 가능 - 현재 ${cam.mode}`);
        if (info.handsUpFunc) info.handsUpFunc("myCam_invisible");
        return;
    }
    const t0 = Date.now();
    info.videoInputInfo.isMyCamRunning = true;
    const ack = await sendSwitch("SCREEN", "front", { role: "myCam" });
    const stream = getIOSRelayStream("myCam");
    if (ack && ack.result === "done" && stream && info.myCamEl) {
        cam.myCamOn = true;
        if (info.myCamEl.srcObject !== stream) info.myCamEl.srcObject = stream;
        info.myCamEl.muted = true;
        const p = info.myCamEl.play();
        if (p && p.catch) p.catch((e) => console.log(`${LOG} MyCam play 오류`, e));
        if (info.handsUpFunc) info.handsUpFunc("myCam_visible");
        console.log(`${LOG} MyCam 켜기 완료 ${ack.inputWidth}x${ack.inputHeight} (${Date.now() - t0}ms)`);
    } else {
        console.log(`${LOG} MyCam 켜기 실패`, ack, `stream[${!!stream}] myCamEl[${!!info.myCamEl}]`);
        if (ack && ack.result === "done") await sendSwitch("SCREEN", "front", { role: "myCam", detach: true });
        cam.myCamOn = false;
        info.videoInputInfo.isMyCamRunning = false;
        info.videoInputInfo.myCamIndex = -1;
        if (info.handsUpFunc) info.handsUpFunc("myCam_invisible");
        if (ctx.setScreenImage) ctx.setScreenImage("connect");
    }
};

/**
 * 마이캠 버튼 처리 (changeMyCamViewInput 대체).
 * @param {Object} ctx - { info: handsUpClientInfo, setScreenImage }
 * @returns {Boolean} 처리했으면 true (false 면 기존 경로)
 */
export const changeIOSRelayMyCam = (ctx, lSeq, uSeq, status) => {
    if (!isIOSRelayCameraReady() || !isIOSRelayFeature("myCam")) return false;
    const info = ctx.info;
    if (lSeq !== info.liveSeq || uSeq !== info.userSeq) return true;
    if (cam.myCamBusy) { console.log(`${LOG} MyCam 처리 중 - 무시`); return true; }
    cam.myCamBusy = true;
    (async () => {
        try {
            if (status) {
                await myCamOn(ctx);
            } else {
                await myCamOff(ctx, "button");
                if (info.handsUpFunc) info.handsUpFunc("myCam_invisible");
                if (cam.mode === "SCREEN" && ctx.setScreenImage) ctx.setScreenImage("connect");
            }
        } catch (e) {
            console.log(`${LOG} MyCam 오류`, e);
        } finally {
            cam.myCamBusy = false;
        }
    })();
    return true;
};

/**
 * clearMyCamViewInput 대체 - relay MyCam 이면 트랙을 멈추지 않고 정리.
 * @returns {Boolean} relay MyCam 을 정리했으면 true (false 면 기존 정리 진행)
 */
export const clearIOSRelayMyCam = (info) => {
    const stream = getIOSRelayStream("myCam");
    const isRelayEl = !!(info.myCamEl && stream && info.myCamEl.srcObject === stream);
    if (!cam.myCamOn && !isRelayEl) return false;
    myCamOff({ info }, "clear");
    return true;
};

// 개발 확인용 (Safari 콘솔: __iosRelayLive.info())
try {
    window.__iosRelayLive = {
        info: () => ({ sessionId: state.sessionId, gen: state.connectionGeneration, ice: state.ice, roles: Object.keys(state.streams), mode: cam.mode, facing: cam.facing, myCam: cam.myCamOn }),
        start: () => startIOSRelaySession("console"),
        stop: () => stopIOSRelaySession("console"),
    };
} catch (e) { }
